import { useCallback } from 'react';
import { useQueryClient } from 'react-query';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router';
import { ProjectId } from 'api/models';
import { Project } from 'api/project';
import { Action, EventType, ObjectType, SocketData } from 'api/pushes';
import { toastService } from 'components/molecules/toast';
import { RoutePaths } from 'constant';
import { removeProjectTasks } from 'data/tasks/slice';
import { getProjectDetailQueryKey } from 'queries/common';
import {
  ALL_PROJECTS_RM_QUERY_KEY,
  PROJECTS_META_QUERY_KEY,
} from 'queries/constants';
import { useProjectWSUpdates } from '../use-project-ws-updates';
import { useTranslation } from '../use-translation';

// @TODO handle indexedDb update here to keep project related changes in sync
export const useProjectDetailWebsocket = ([, projectId]: ProjectId): void => {
  const queryClient = useQueryClient();
  const dispatch = useDispatch();
  const history = useHistory();
  const deleteTitle = useTranslation('project.info.delete.title');
  const deleteMessage = useTranslation('project.info.delete.message');
  const handleProjectSocketUpdates = useCallback(
    async (socketData: SocketData<Project>) => {
      const { projectId: projectIdFromSocket, action } = socketData;

      switch (action) {
        case Action.Duplicate:
          await queryClient.invalidateQueries(
            getProjectDetailQueryKey(ProjectId(socketData.projectId)),
          );
          break;
        case Action.Create:
        case Action.Update:
          await queryClient.invalidateQueries(ALL_PROJECTS_RM_QUERY_KEY);
          await queryClient.invalidateQueries(PROJECTS_META_QUERY_KEY);
          await queryClient.invalidateQueries(
            getProjectDetailQueryKey(ProjectId(projectIdFromSocket)),
          );
          break;
        case Action.Delete:
          toastService({ title: deleteTitle, message: deleteMessage }).info();
          await queryClient.invalidateQueries(ALL_PROJECTS_RM_QUERY_KEY);
          dispatch(removeProjectTasks([projectIdFromSocket]));
          history.push(RoutePaths.projects);
          break;
        default:
          break;
      }
    },
    [queryClient, dispatch, history, deleteTitle, deleteMessage],
  );

  useProjectWSUpdates(
    projectId,
    handleProjectSocketUpdates,
    EventType(Action.Create, ObjectType.Project),
  );

  useProjectWSUpdates(
    projectId,
    handleProjectSocketUpdates,
    EventType(Action.Update, ObjectType.Project),
  );

  useProjectWSUpdates(
    projectId,
    handleProjectSocketUpdates,
    EventType(Action.Delete, ObjectType.Project),
  );
  useProjectWSUpdates(
    projectId,
    handleProjectSocketUpdates,
    EventType(Action.Duplicate, ObjectType.Task),
  );
};
